import { useEffect, useRef, useState } from 'react';
import { STATE_PALETTE } from '../../utils/statePalette';

export interface StateColorPickerPopoverProps {
  /** Currently-selected hex (highlighted with a thicker ring). May be any hex — non-palette values render no highlight. */
  currentColor: string;
  /** Called when the user clicks a palette swatch or commits a custom hex. Followed immediately by onClose(). */
  onPick: (hex: string) => void;
  /** Called on click-outside, Escape, or after a swatch click. */
  onClose: () => void;
  /** Optional override for the popover container className (parent positioning). */
  className?: string;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Click-to-pick palette popover (Phase 20 D-09 + custom-hex extension).
 *
 * Renders the 5-color Phase 17 D-11 palette as swatch buttons plus a
 * native color input for custom hex values when 5 colors aren't enough
 * (e.g. templates with 6+ states). Palette swatches stay deterministic
 * for seeded templates; the custom input is an escape hatch.
 *
 * Used by:
 *   - <StateListEditor> on each state row's color swatch click
 *     (frontend/src/components/domain/StateListEditor.tsx, plan 20-05).
 */
export function StateColorPickerPopover({
  currentColor,
  onPick,
  onClose,
  className,
}: StateColorPickerPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [hexInput, setHexInput] = useState(
    HEX_RE.test(currentColor) ? currentColor.toLowerCase() : '#000000',
  );
  const [hexError, setHexError] = useState(false);

  useEffect(() => {
    const handleMousedown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMousedown);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('mousedown', handleMousedown);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [onClose]);

  const commitCustomHex = (raw: string) => {
    const v = raw.trim().toLowerCase();
    const normalized = v.startsWith('#') ? v : `#${v}`;
    if (HEX_RE.test(normalized)) {
      onPick(normalized);
      onClose();
    } else {
      setHexError(true);
    }
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Choose color"
      className={
        className ??
        'absolute top-full mt-2 left-0 z-50 flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg'
      }
    >
      <div className="flex gap-1">
        {STATE_PALETTE.map((hex) => {
          const selected = hex.toLowerCase() === currentColor.toLowerCase();
          return (
            <button
              key={hex}
              type="button"
              aria-label={hex}
              aria-pressed={selected}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => {
                onPick(hex);
                onClose();
              }}
              className={`h-6 w-6 rounded-full border transition-shadow ${
                selected
                  ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1'
                  : 'border-gray-300 hover:ring-2 hover:ring-gray-400 hover:ring-offset-1'
              }`}
              style={{ backgroundColor: hex }}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-1.5 border-t border-gray-100 pt-2">
        <label className="flex items-center gap-1.5">
          <span className="sr-only">Pick custom color</span>
          <input
            type="color"
            value={HEX_RE.test(hexInput) ? hexInput : '#000000'}
            onChange={(e) => {
              setHexError(false);
              setHexInput(e.target.value.toLowerCase());
            }}
            aria-label="Custom color picker"
            className="h-6 w-6 cursor-pointer rounded border border-gray-300 bg-white p-0"
          />
        </label>
        <input
          type="text"
          value={hexInput}
          onChange={(e) => {
            setHexError(false);
            setHexInput(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitCustomHex(hexInput);
            }
          }}
          placeholder="#aabbcc"
          aria-label="Custom hex value"
          aria-invalid={hexError || undefined}
          className={`h-6 w-20 rounded border px-1.5 font-mono text-xs ${
            hexError ? 'border-red-500 text-red-600' : 'border-gray-300'
          }`}
        />
        <button
          type="button"
          onClick={() => commitCustomHex(hexInput)}
          className="rounded bg-primary-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-primary-700"
        >
          Set
        </button>
      </div>
    </div>
  );
}
